import React from "react";
import { NameResult } from "../../types";
import { useAtomValue } from "jotai";
import { userAtom } from "../../store/store";
import { useNavigate } from "react-router-dom";
import EmptyState from "./EmptyState";
import LoadingState from "./LoadingState";
import Card from "../common/Card/Card";
import Button from "../common/Button/Button";
import GenderIcon from "../common/GenderIcon/GenderIcon";
import "./ResultDisplay.css";

interface ResultDisplayProps {
  results: NameResult[];
  isGenerating: boolean;
  generatingProgress?: number;
  onSave: (result: NameResult) => void;
  onTryAgain: (result: NameResult) => void;
  onLogin: () => void;
  hasDuplicateName: boolean;
}

const ResultDisplay: React.FC<ResultDisplayProps> = ({
  results,
  isGenerating,
  generatingProgress = 0,
  onSave,
  onTryAgain,
  onLogin,
  hasDuplicateName,
}) => {
  const user = useAtomValue(userAtom);
  const navigate = useNavigate();
  const MAX_DISPLAY_RESULTS = 10;

  const handleSave = (result: NameResult) => {
    if (!user.isLogin) {
      onLogin(); // 这将打开登录弹框
      return;
    }
    onSave(result);
  };

  const handleViewMore = () => {
    navigate("/records");
  };

  // 限制显示的结果数量
  const displayResults = results.slice(0, MAX_DISPLAY_RESULTS);
  const hasMoreResults = results.length > MAX_DISPLAY_RESULTS;

  return (
    <div className="result-display">
      {isGenerating ? (
        <LoadingState progress={generatingProgress} />
      ) : results.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="results-list">
          {hasDuplicateName && (
            <div className="duplicate-name-alert">
              <p>
                You've received a similar name before. Try different options
                combinations to get more varied results.
              </p>
            </div>
          )}

          {displayResults.map((result) => (
            <Card key={result.id} className="result-item" elevation="sm">
              <div className="result-content">
                {result.firstName && (
                  <div className="name-gender-container">
                    <p className="original-name">
                      {result.firstName} {result.lastName}
                    </p>
                    <GenderIcon gender={result.gender} />
                  </div>
                )}
                <h2 className="chinese-name">{result.chineseName}</h2>
                <p className="pinyin">{result.pinyin}</p>
                <div className="tags">
                  {[
                    ...result.styles,
                    ...result.themes,
                    ...result.meanings,
                    ...result.phonetics,
                  ].map((tag, index) => (
                    <span key={index} className="tag">
                      {tag.label}
                    </span>
                  ))}
                </div>
                <p className="meaning-explanation">{result.explanation}</p>
                <div className="result-actions">
                  <Button
                    variant={result.status === 1 ? "secondary" : "primary"}
                    size="md"
                    onClick={() => handleSave(result)}
                  >
                    {result.status === 1 ? "Unsave" : "Save"}
                  </Button>
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={() => onTryAgain(result)}
                  >
                    Try Again
                  </Button>
                </div>
              </div>
            </Card>
          ))}

          {hasMoreResults && (
            <div className="view-more-container">
              <Button
                variant="secondary"
                size="lg"
                onClick={handleViewMore}
                fullWidth
              >
                View More Records
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ResultDisplay;
